import { is, OrderedMap, Seq } from 'immutable';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { iDevicePing, iFullStoreState, iLocation, Units } from '../../../shared/interfaces';
import { IPointsContainer } from '../../../stores/reducers/devicesTripsPoints';
import { IReducerSelectedPoint } from '../../../stores/reducers/selectedPoint';
import { removeSelectedPoint, setSelectedPoint } from '../../../stores/reducers/selectedPoint/AC';
import { selectFirstPointInNextTrip } from '../../../stores/sagas/selectFirstPointInNextTrip';
import { selectLastPointInPrevTrip } from '../../../stores/sagas/selectLastPointInPrevTrip';
import { store } from "../../../stores/store";
import { shouldShowPoint } from '../../gmap/pois';
import { useMapTrip } from '../store/MapTripsContext';
import CustomInfoWindow from "./CustomInfoWindow";

type IProps = {
    firstTripId?: string;
    lastTripId?: string;
}

const InfoSelectedPoint = ({firstTripId, lastTripId}: IProps) => {
    const dispatch = useDispatch();
    const {trip} = useMapTrip() || {};
    const isReplaying = useSelector<iFullStoreState, boolean>((s) => s.gmap.replayPath);
    const selectedPoint = useSelector<iFullStoreState, IReducerSelectedPoint>(state => state.selectedPoint);
    const showPOI = useSelector<iFullStoreState, boolean>(s => s.gmap.showPOI);
    const showDots = useSelector<iFullStoreState, boolean>(s => s.gmap.showDots);
    const replayPoint = useSelector<iFullStoreState, iDevicePing | null>(state => state.replay?.point);

    const point = useSelector<iFullStoreState, iDevicePing | undefined>(
        state => {
          const deviceId = selectedPoint?.deviceId;
          const tripId = selectedPoint?.tripId;
          const pointId = selectedPoint?.pointId;
          return deviceId && tripId && pointId
            ? state.devicesTripsPoints.getIn([deviceId, 'tripsPoints', tripId, pointId]) as iDevicePing
            : undefined;
        },
        (left, right) => left?.pointId === right?.pointId,
    );
    const deviceLastPing = useSelector<iFullStoreState, iDevicePing | undefined | null>(
        (state) => state.devicesData.devicesLastPing.get(selectedPoint?.deviceId),
        (l, r) => typeof l === typeof r && l?.pointId === r?.pointId,
    );

    const tripPoints = useSelector<iFullStoreState, IPointsContainer | undefined>(
        state => {
            if (!selectedPoint) return undefined;

            let points: IPointsContainer = state.devicesTripsPoints.getIn(
                [selectedPoint.deviceId, 'tripsPoints', selectedPoint.tripId],
                OrderedMap(),
            ) as IPointsContainer;

            if (!showDots || !showPOI) {
                points = points.filter(shouldShowPoint({showDots, showPOI}));
            }
            return points;
        },
        (left, right) => {
            if (typeof left === 'undefined' || typeof right === 'undefined') {
                return !!left === !!right;
            }
            return is(left.keySeq(), right.keySeq());
        },
    );

    const timezone = useSelector<iFullStoreState, string>(
        state => state.devicesData.devicesDetails.getIn([selectedPoint?.deviceId, 'timezone'], 'EST') as string
    );

    const units = useSelector<iFullStoreState, Units>(
        state => state.devicesData.devicesDetails.getIn(
            [selectedPoint?.deviceId, 'units'],
            Units.miles,
        ) as Units);

    // Only show info window when a point was explicitly clicked by the user

    // The isClicked flag is set to true only when a user directly clicks on a point
    if (!selectedPoint || selectedPoint.isClicked !== true) return null;

    let prevPointId: string | undefined;
    let nextPointId: string | undefined;
    if (tripPoints) {
        const prevLastTuple = findPrevNextElements(tripPoints.keySeq(), selectedPoint.pointId);
        if (prevLastTuple) {
            [prevPointId, nextPointId] = prevLastTuple;
        }
    }

    const existsPrevPoint = !!(prevPointId ?? (!!firstTripId ? firstTripId !== selectedPoint?.tripId : false));
    const existsNextPoint = !!(nextPointId ?? (!!lastTripId ? lastTripId !== selectedPoint?.tripId : false));

    const selectPrevPoint = () => {
        const selectedPoint = store.getState().selectedPoint;
        const isClicked = selectedPoint.isClicked || false;

        if (prevPointId) {
            dispatch(setSelectedPoint(selectedPoint.deviceId, selectedPoint.tripId, prevPointId, tripPoints?.getIn([prevPointId, 'coordinates', 'location']) as iLocation, isClicked));
        } else {
            dispatch(selectLastPointInPrevTrip(selectedPoint.deviceId, selectedPoint.tripId));
        }
    };
    const selectNextPoint = () => {
        const selectedPoint = store.getState().selectedPoint;
        const isClicked = selectedPoint.isClicked || false;

        if (nextPointId) {
            dispatch(setSelectedPoint(selectedPoint.deviceId, selectedPoint.tripId, nextPointId, tripPoints?.getIn([nextPointId, 'coordinates', 'location']) as iLocation, isClicked));
        } else {
            dispatch(selectFirstPointInNextTrip(selectedPoint.deviceId, selectedPoint.tripId));
        }
    };

    const hasExtraInfo = point || deviceLastPing;

    return (
      <CustomInfoWindow
        position={selectedPoint.position}
        onCloseClick={() => dispatch(removeSelectedPoint())}
        selectedPoint={selectedPoint}
        dispatch={dispatch}
        hasExtraInfo={hasExtraInfo}
        point={point}
        deviceLastPing={deviceLastPing}
        units={units}
        timezone={timezone}
        existsPrevPoint={existsPrevPoint}
        existsNextPoint={existsNextPoint}
        selectPrevPoint={selectPrevPoint}
        selectNextPoint={selectNextPoint}
      />
    );
}

const findPrevNextElements = (collection: Seq.Indexed<string>, element: string | undefined): undefined | [string | undefined, string | undefined] => {
    if (!collection.size || !element) return;

    let nextElement;
    let prevElement;

    const currentElementIndex = collection.findIndex(el => element === el);
    if (currentElementIndex === -1) return;

    if (currentElementIndex < collection.size - 1) {
        nextElement = collection.get(currentElementIndex + 1);
    }
    if (currentElementIndex > 0) {
        prevElement = collection.get(currentElementIndex - 1);
    }

    return [prevElement, nextElement];
};

export default InfoSelectedPoint